// Workspaces (02-DESIGN/12-workspace-hierarchy/01-the-relation.md § the
// Workspaces page), in two tabs. **Workspaces**: an admin of a workspace that
// may manage workspaces sees its children as rows — the display name and
// slug, the first admin's handle while that row is pending, how many
// workspaces are beneath it, the **blueprint** it uses (*updating* while
// behind it, *needs a secret* while a slot has no value), and its state —
// and on a row its page, *Transfer* and *Detach*. *New workspace* takes the
// slug, the display name, the first admin's handle and which named blueprint
// to apply, or none, and is refused before submit on the limit, read from the
// caller's own values; it creates the child and then assigns the blueprint,
// and a child whose assignment did not land is left with *Apply a blueprint*.
// A managed child's state is the one its steward last attested, read from the
// blueprint service and verified by this page against the carrier keys it
// holds (02-DESIGN/13-blueprints/01-the-blueprint.md § the console:
// Workspaces). **Blueprints**: the parent's library of named blueprints, each
// with how many workspaces use it, and *New blueprint* (§ named blueprints;
// decision 0052). Each child's row also carries its **recovery share** — the
// parent's share of the child's keys: its state, and the child's open
// recovery, read through the child.<slug> escrow verbs
// (02-DESIGN/02-secrets/03-escrow.md § what the console shows); *Collect*
// and *Present* are the child's Zero-trust tab's, and the row's *Recovery
// shares* opens the child's page on that tab (spec 061). Every child's row
// opens its page, managed or solo, and its menu carries *Open Northwind*, the
// way into the child at its own address; the blueprint on a row is a link to
// the blueprint's page. *New workspace*, *New blueprint*, *Apply a
// blueprint* and *Transfer* are each a form in the page's place, with no
// address of its own (02-DESIGN/03-console/01-the-console.md § conventions: a
// form; decision 0069).

import { Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";

/** The page's forms, named in the history entry's state: a form has no address. */
const NEW_WORKSPACE = "new-workspace";
const NEW_BLUEPRINT = "new-blueprint";
const APPLY = "apply:";
const TRANSFER = "transfer:";

/** formChild is the child a row's form names — `apply:acme-ml` — among the children listed. */
export function formChild<C extends { slug: string }>(
  open: string | undefined,
  prefix: string,
  children: readonly C[],
): C | undefined {
  if (open?.startsWith(prefix) !== true) {
    return undefined;
  }
  const slug = open.slice(prefix.length);
  return children.find((c) => c.slug === slug);
}

import { reservedWhy, validSlug } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  Empty,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  plural,
  Select,
  TextInput,
  useLoad,
  when,
} from "../../ui/components.tsx";
import {
  Code as BlueprintCode,
  MAX_LIBRARY,
  SERVICE as BLUEPRINT,
  Subjects as BlueprintSubjects,
  validName,
  type ChildRow,
  type ChildrenReply,
  type Entry,
  type LibraryReply,
  type LibraryRow,
  type StatusReply,
} from "../../wire/blueprint.ts";
import {
  Feature,
  Limit,
  PlanCode,
  RelationCode,
  Subjects,
  validDisplayName,
  type Child,
  type ChildReply,
  type ChildrenCreateRequest,
  type ChildrenListReply,
  type ChildrenTransferRequest,
  type TenantReply,
} from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { Table } from "../../ui/table.tsx";
import { Tabs } from "../../ui/tabs.tsx";
import { CouldNotShow } from "../../ui/boundary.tsx";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { NameLink } from "../../ui/ident.tsx";
import { featureRefusal, fromPlan, holderOf, limitSentence, planRefusal } from "../settings/plan.ts";
import { PlanLink, usePlan } from "../settings/PlanNotes.tsx";
import { useSurface } from "../surface.tsx";
import { useMoves } from "../workspace/moves.tsx";
import { attest, rowState, type Attested } from "./blueprint.ts";
import { RecoveryPill, useChildEscrow } from "./ChildRecovery.tsx";
import { BLUEPRINTS_TAB, blueprintFailure, blueprintPath, childPath, usesWords } from "./library.ts";
import { parentSlot, RECOVERY_LINK, recoveryCell, ZERO_TRUST_TAB } from "./recovery.ts";
import {
  childName,
  childState,
  managesWorkspaces,
  relationRefusal,
  roomForAnother,
  transferTargets,
} from "./relation.ts";

/** Refused shows a refusal of the parent's verbs in the relation's words where it has them. */
function Refused({ err, what }: { err: unknown; what: string }) {
  // A refusal the plan caused names the plan and links to it (02-DESIGN/17-plans/02-features-and-limits.md § the console).
  const plan = usePlan();
  const s = useSurface();
  const code = typeof err === "object" && err !== null && "code" in err ? err.code : undefined;
  const byPlan =
    code === PlanCode.notInPlan ||
    code === PlanCode.licenseExpired ||
    (code === RelationCode.limit && fromPlan(plan.tenant, Limit.workspaces));
  const planSaid = byPlan ? planRefusal(err, plan.holder, s.name, Feature.manageWorkspaces) : undefined;
  if (planSaid !== undefined) {
    return (
      <Notice kind="bad" title={`${what}: refused`}>
        {planSaid} <PlanLink />
      </Notice>
    );
  }
  const said = relationRefusal(err);
  if (said === undefined) {
    return <NotAllowed err={err} what={what} />;
  }
  return (
    <Notice kind="bad" title={`${what}: refused`}>
      {said}
    </Notice>
  );
}

export function WorkspacesPage() {
  const s = useSurface();
  // A tab is a view of the page and never in the address (01-the-console.md §
  // conventions): the page opens on *Workspaces*, or on the tab a link from
  // a blueprint's page carries in its navigation state.
  const location = useLocation();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<string>(
    (location.state as { tab?: string } | null)?.tab === BLUEPRINTS_TAB ? BLUEPRINTS_TAB : "workspaces",
  );
  const [tick, setTick] = useState(0);
  const [self] = useLoad(
    "tenant",
    () => s.bus.call<TenantReply>(SERVICE, Subjects.tenantGet(), undefined),
    tick,
  );
  const [children] = useLoad(
    "children",
    () => s.bus.call<ChildrenListReply>(SERVICE, Subjects.childrenList(), undefined),
    tick,
  );
  // Which blueprint each child uses, and the library: the blueprint service's.
  const [uses] = useLoad(
    "blueprint-children",
    () => s.bus.call<ChildrenReply>(BLUEPRINT, BlueprintSubjects.children(), undefined),
    tick,
  );
  const [library] = useLoad(
    "library",
    () => s.bus.call<LibraryReply>(BLUEPRINT, BlueprintSubjects.libraryList(), undefined),
    tick,
  );
  const form = useFormState();
  // *New workspace* is the Workspaces tab's form and *New blueprint* the
  // Blueprints tab's: each stands in its tab's place with the tabs kept, and
  // picking the other tab closes it.
  const tab =
    form.open === NEW_BLUEPRINT ? BLUEPRINTS_TAB : form.open === NEW_WORKSPACE ? "workspaces" : chosen;
  const tabForm = form.open === NEW_WORKSPACE || form.open === NEW_BLUEPRINT;
  const setTab = (t: string) => {
    if (tabForm) {
      form.close();
    }
    setChosen(t);
  };
  const moves = useMoves();
  const [err, setErr] = useState<unknown>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  // The workspace just created, whose blueprint the note leads into.
  const [created, setCreated] = useState<string | undefined>(undefined);
  const reload = () => {
    setTick((t) => t + 1);
  };
  const tenant = self.state === "ready" ? self.value.tenant : undefined;
  const room = roomForAnother(tenant?.limits);
  const list = children.state === "ready" ? children.value.children : [];
  const rows = new Map(
    (uses.state === "ready" ? (uses.value.children ?? []) : []).map((r): [string, ChildRow] => [r.slug, r]),
  );
  const blueprints = library.state === "ready" ? (library.value.blueprints ?? []) : [];
  const escrow = useChildEscrow(
    list.map((c) => c.slug),
    tick,
  );
  const applying = formChild(form.open, APPLY, list);
  const moving = formChild(form.open, TRANSFER, list);

  if (self.state === "ready" && !managesWorkspaces(tenant)) {
    const planHolds =
      tenant !== undefined &&
      fromPlan(tenant, Feature.manageWorkspaces) &&
      tenant.entitlement?.features[Feature.manageWorkspaces]?.granted !== true;
    return (
      <>
        <PageHead title="Workspaces" line={`The workspaces ${s.name} manages.`} />
        {planHolds ? (
          <Empty>
            {featureRefusal(Feature.manageWorkspaces, holderOf(tenant, s.name).name, s.name)} <PlanLink />
          </Empty>
        ) : (
          <Empty>{s.name} may not manage workspaces.</Empty>
        )}
      </>
    );
  }

  const detach = (c: Child) => {
    setErr(undefined);
    s.bus.call<ChildReply>(SERVICE, Subjects.childrenDetach(c.slug), undefined).then(
      () => {
        setCreated(undefined);
        setNote(`${childName(c)} is detached: it is a workspace of its own, not managed.`);
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };

  const newWorkspace = (
    <Button
      kind="primary"
      disabled={!room}
      onClick={() => {
        form.show(NEW_WORKSPACE);
      }}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      New workspace
    </Button>
  );
  const newBlueprint = (
    <Button
      kind="primary"
      disabled={blueprints.length >= MAX_LIBRARY}
      onClick={() => {
        form.show(NEW_BLUEPRINT);
      }}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      New blueprint
    </Button>
  );

  // A form stands in the page's place; the list returns, the row marked, when it closes.
  if (applying !== undefined) {
    return (
      <Apply
        child={applying}
        blueprints={blueprints}
        onClose={form.close}
        onApplied={(named) => {
          form.close();
          setCreated(applying.slug);
          setNote(
            `${childName(applying)} uses the blueprint ${named} now; its steward is placed and converges on it.`,
          );
          reload();
        }}
      />
    );
  }
  if (moving !== undefined) {
    return (
      <Transfer
        child={moving}
        targets={transferTargets(moving.slug, list, tenant)}
        onClose={form.close}
        onMoved={(to) => {
          form.close();
          setCreated(undefined);
          setNote(
            `${childName(moving)} is handed to ${to}: not managed until that workspace applies one of its blueprints.`,
          );
          reload();
        }}
      />
    );
  }
  return (
    <>
      <PageHead
        title="Workspaces"
        line={`The workspaces ${s.name} manages, and the blueprints it keeps for them.`}
        action={tabForm ? undefined : tab === BLUEPRINTS_TAB ? newBlueprint : newWorkspace}
      />
      <Tabs
        label="Workspaces"
        tabs={[
          { id: "workspaces", label: "Workspaces" },
          { id: BLUEPRINTS_TAB, label: "Blueprints" },
        ]}
        value={tab}
        onChange={setTab}
      >
        {form.open === NEW_BLUEPRINT ? (
          <NameBlueprint
            taken={blueprints.map((b) => b.name)}
            onClose={form.close}
            onNamed={(named) => {
              // The editor opens on the name, empty, and its first save creates it.
              void navigate(blueprintPath(s.tenant, named), { replace: true });
            }}
          />
        ) : form.open === NEW_WORKSPACE ? (
          <NewWorkspace
            blueprints={blueprints}
            onClose={form.close}
            onCreated={(c, applied, assignErr) => {
              form.close();
              setTab("workspaces");
              setCreated(c.slug);
              setNote(
                `${childName(c)} is created${applied !== undefined ? ` with the blueprint ${applied}` : ""}. ${c.pending ?? "Its first admin"} is its first admin, waiting until their first sign-in.`,
              );
              if (assignErr !== undefined) {
                setErr(assignErr);
              }
              reload();
            }}
          />
        ) : tab === BLUEPRINTS_TAB ? (
          <Library state={library} blueprints={blueprints} newBlueprint={newBlueprint} onRetry={reload} />
        ) : (
          <>
            {!room &&
              tenant?.limits !== undefined &&
              (fromPlan(tenant, Limit.workspaces) ? (
                <Notice kind="info">
                  {limitSentence(
                    Limit.workspaces,
                    holderOf(tenant, s.name).name,
                    tenant.limits.workspaces,
                    0,
                  )}{" "}
                  A new workspace needs room first. <PlanLink />
                </Notice>
              ) : (
                <Notice kind="info">
                  {s.name} holds {plural(tenant.limits.beneath, "workspace")} beneath it, its limit. A new
                  workspace needs room first.
                </Notice>
              ))}
            {err !== undefined && <Refused err={err} what="Workspaces" />}
            {note !== undefined && (
              <Notice kind="ok">
                {note}{" "}
                {created !== undefined && (
                  <>
                    <Link className="text-link" to={childPath(s.tenant, created)}>
                      Open its page
                    </Link>{" "}
                    to see its blueprint and enter the secrets it needs.{" "}
                  </>
                )}
                <button
                  type="button"
                  className="text-link"
                  onClick={() => {
                    setNote(undefined);
                    setCreated(undefined);
                  }}
                >
                  Dismiss
                </button>
              </Notice>
            )}
            {children.state === "pending" && <Pending what="Reading the workspaces you manage" />}
            {children.state === "refused" && <NotAllowed err={children.err} what="Workspaces" />}
            {children.state === "ready" && list.length === 0 && (
              <Empty action={room ? newWorkspace : undefined}>
                {s.name} manages no workspace yet. Keep a blueprint under <strong>Blueprints</strong> — the
                sign-in a workspace uses, the settings and guardrails it cannot change, the models, toolkits
                and solutions it must run, the secrets they need and the limits it lives within — then create
                a workspace with <strong>New workspace</strong> and choose it. A workspace with no blueprint
                is a workspace of its own.
              </Empty>
            )}
            {list.length > 0 && (
              <Table
                label="Workspaces"
                rows={list}
                rowKey={(c) => c.slug}
                sort={{ key: "workspace", dir: "asc" }}
                to={(c) => childPath(s.tenant, c.slug)}
                mark={created}
                columns={[
                  {
                    key: "workspace",
                    header: "Workspace",
                    width: "minmax(0, 1.6fr)",
                    value: (c) => childName(c),
                    text: (c) => `${childName(c)} ${c.slug}`,
                    cell: (c) => (
                      <span className="cell">
                        <span className="cell-main">{childName(c)}</span>
                        <span className="cell-sub mono">{c.slug}</span>
                      </span>
                    ),
                  },
                  {
                    key: "admin",
                    header: "First admin",
                    narrow: false,
                    value: (c) => (c.pending !== undefined && c.pending !== "" ? c.pending : "signed in"),
                    cell: (c) => (
                      <span className="muted">
                        {c.pending !== undefined && c.pending !== "" ? (
                          <>
                            <span className="mono">{c.pending}</span> <Pill>waiting</Pill>
                          </>
                        ) : (
                          "signed in"
                        )}
                      </span>
                    ),
                  },
                  {
                    key: "beneath",
                    header: "Beneath it",
                    width: "120px",
                    narrow: false,
                    value: (c) => c.descendants,
                    text: (c) => plural(c.descendants, "workspace"),
                    cell: (c) => <span className="muted">{plural(c.descendants, "workspace")}</span>,
                  },
                  {
                    key: "blueprint",
                    header: "Blueprint",
                    // At a phone's width the blueprint and the state are on the child's page.
                    narrow: false,
                    facet: "Blueprint",
                    value: (c) => usesWords(rows.get(c.slug), c.managed).name,
                    cell: (c) =>
                      uses.state === "refused" ? (
                        <span className="muted">not read</span>
                      ) : (
                        <Uses tenant={s.tenant} row={rows.get(c.slug)} managed={c.managed} />
                      ),
                  },
                  {
                    key: "state",
                    header: "State",
                    narrow: false,
                    facet: "Status",
                    value: (c) => childState(c),
                    cell: (c) => (c.managed ? <Attestation child={c} /> : <Pill>{childState(c)}</Pill>),
                  },
                  {
                    key: "recovery",
                    header: "Recovery share",
                    facet: "Recovery share",
                    value: (c) => recoveryCell(escrow.get(c.slug), when).text,
                    cell: (c) => <RecoveryPill e={escrow.get(c.slug)} />,
                  },
                ]}
                menu={(c) => [
                  ...(moves === undefined
                    ? []
                    : [
                        {
                          label: `Open ${childName(c)}`,
                          onSelect: () => {
                            moves.enter(c.slug);
                          },
                        },
                      ]),
                  // The parent's share's acts are the child's Zero-trust tab's: the row opens it.
                  ...(parentSlot(escrow.get(c.slug)?.status) === undefined
                    ? []
                    : [
                        {
                          label: RECOVERY_LINK,
                          onSelect: () => {
                            void navigate(childPath(s.tenant, c.slug), { state: { tab: ZERO_TRUST_TAB } });
                          },
                        },
                      ]),
                  ...(c.managed || blueprints.length === 0
                    ? []
                    : [
                        {
                          label: "Apply a blueprint",
                          onSelect: () => {
                            setErr(undefined);
                            form.show(`${APPLY}${c.slug}`);
                          },
                        },
                      ]),
                  {
                    label: "Transfer",
                    onSelect: () => {
                      setErr(undefined);
                      form.show(`${TRANSFER}${c.slug}`);
                    },
                  },
                  {
                    label: "Detach",
                    danger: true,
                    confirm: {
                      title: `Detach ${childName(c)}?`,
                      consequence: `${childName(c)} becomes a workspace of its own, with what is beneath it. Its steward first lowers what ${s.name}'s blueprint put there; what its admins wrote stays theirs.`,
                    },
                    onSelect: () => {
                      detach(c);
                    },
                  },
                ]}
              />
            )}
          </>
        )}
      </Tabs>
    </>
  );
}

/** Attestation is a managed child's state: what its steward last attested, as this page verified it. */
function Attestation({ child }: { child: Child }) {
  const s = useSurface();
  const [a] = useLoad(`attested:${child.slug}`, async (): Promise<Attested> => {
    let status: StatusReply | undefined;
    try {
      status = await s.bus.call<StatusReply>(BLUEPRINT, BlueprintSubjects.status(child.slug), undefined);
    } catch (err) {
      if (!isRefusal(err, BlueprintCode.notFound)) {
        throw err;
      }
    }
    return attest(status, await s.bus.carrierKeys(), child.slug);
  });
  if (a.state !== "ready") {
    return <Pill strong>{childState(child)}</Pill>;
  }
  const said = rowState(undefined, true, a.value, when);
  const tone =
    a.value.state === "attested" && !a.value.silent ? "ok" : a.value.state === "not-yet" ? "neutral" : "bad";
  return (
    <Pill tone={tone} strong>
      {said}
    </Pill>
  );
}

/** NONE is the blueprint picker's *none*: the workspace is created on its own. */
const NONE = "";

/** blueprintOptions is the picker: none, then the library by name with how many use each. */
function blueprintOptions(blueprints: readonly LibraryRow[], none: string | undefined) {
  const named = blueprints.map((b) => ({
    value: b.name,
    label: b.children === 0 ? b.name : `${b.name} (${plural(b.children, "workspace")})`,
  }));
  return none === undefined ? named : [{ value: NONE, label: none }, ...named];
}

function NewWorkspace({
  blueprints,
  onClose,
  onCreated,
}: {
  blueprints: readonly LibraryRow[];
  onClose: () => void;
  /** Called once the workspace exists: the blueprint applied, or the refusal of its assignment. */
  onCreated: (c: Child, applied: string | undefined, assignErr: unknown) => void;
}) {
  const s = useSurface();
  const { platform } = usePage();
  const lane = platform.platform_provider.lane;
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [admin, setAdmin] = useState("");
  const [chosen, setChosen] = useState<string>(blueprints[0]?.name ?? NONE);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const reserved = reservedWhy(slug);
  const valid =
    validSlug(slug) && (name.trim() === "" || validDisplayName(name.trim())) && admin.trim() !== "";
  const submit = () => {
    if (busy || !valid) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    const body: ChildrenCreateRequest = { slug, admin: admin.trim() };
    if (name.trim() !== "") {
      body.name = name.trim();
    }
    s.bus.call<ChildReply>(SERVICE, Subjects.childrenCreate(), body).then(
      (reply) => {
        if (chosen === NONE) {
          setBusy(false);
          onCreated(reply.child, undefined, undefined);
          return;
        }
        // The blueprint is the blueprint service's: a second call, whose
        // refusal leaves the workspace with no blueprint and *Apply a blueprint*.
        s.bus.call<Entry>(BLUEPRINT, BlueprintSubjects.assign(reply.child.slug), { blueprint: chosen }).then(
          () => {
            setBusy(false);
            onCreated(reply.child, chosen, undefined);
          },
          (e: unknown) => {
            setBusy(false);
            onCreated(reply.child, undefined, e);
          },
        );
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <FormPage
      heading="h2"
      back="Workspaces"
      title="New workspace"
      line="A workspace you manage: its people are its own, and nobody here is a member of it. Its first admin appears as waiting until their first sign-in. The blueprint you choose says what it must look like."
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !valid}>
            {busy ? "Creating…" : "Create"}
          </Button>
        </>
      }
    >
      {err !== undefined && <Refused err={err} what="Create" />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field
          label="Address"
          hint={
            reserved !== undefined
              ? `${reserved}.`
              : "Lowercase letters, digits and hyphens, beginning with a letter. It never changes."
          }
        >
          <TextInput value={slug} onChange={setSlug} placeholder="acme-ml" mono required autoFocus />
        </Field>
        <Field label="Name" hint="What people call it; the address stands in when it is empty.">
          <TextInput value={name} onChange={setName} placeholder="Acme ML" />
        </Field>
        <Field
          label="First admin"
          hint={
            lane === "github"
              ? "Their GitHub login."
              : "Their email address — the one they verify when they sign in."
          }
        >
          <TextInput
            value={admin}
            onChange={setAdmin}
            placeholder={lane === "github" ? "alice" : "alice@example.com"}
            mono
            required
          />
        </Field>
        <Field
          label="Blueprint"
          hint={
            blueprints.length === 0
              ? "You keep no blueprint yet: it is created on its own, and you can apply one later."
              : chosen === NONE
                ? "It is created on its own, and you can apply a blueprint later."
                : "Its steward is placed and makes it what the blueprint says. Secrets the blueprint needs are entered on its page once it has reported."
          }
        >
          <Select value={chosen} onChange={setChosen} options={blueprintOptions(blueprints, "None")} />
        </Field>
      </form>
    </FormPage>
  );
}

/** Uses is a child's Blueprint column: a named blueprint is a link to its page. */
function Uses({ tenant, row, managed }: { tenant: string; row: ChildRow | undefined; managed: boolean }) {
  const u = usesWords(row, managed);
  const named = row?.blueprint !== undefined && row.blueprint !== "" ? row.blueprint : undefined;
  return (
    <span className="cell">
      {named !== undefined ? (
        <span className="cell-main mono">
          <NameLink to={blueprintPath(tenant, named)}>{u.name}</NameLink>
        </span>
      ) : (
        <span className="muted">{u.name}</span>
      )}
      {u.notes.length > 0 && (
        <span className="cell-sub">
          {u.notes.map((n) => (
            <Pill key={n} tone={n === "needs a secret" ? "bad" : "info"}>
              {n}
            </Pill>
          ))}
        </span>
      )}
    </span>
  );
}

/** Apply assigns a named blueprint to a child that has none. */
function Apply({
  child,
  blueprints,
  onClose,
  onApplied,
}: {
  child: Child;
  blueprints: readonly LibraryRow[];
  onClose: () => void;
  onApplied: (name: string) => void;
}) {
  const s = useSurface();
  const [chosen, setChosen] = useState(blueprints[0]?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const submit = () => {
    if (busy || chosen === "") {
      return;
    }
    setBusy(true);
    setErr(undefined);
    s.bus.call<Entry>(BLUEPRINT, BlueprintSubjects.assign(child.slug), { blueprint: chosen }).then(
      () => {
        setBusy(false);
        onApplied(chosen);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <FormPage
      back="Workspaces"
      title={`Apply a blueprint to ${childName(child)}`}
      line="The workspace becomes managed: its steward is placed and makes it what the blueprint says. What the blueprint requires its admins cannot change."
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || chosen === ""}>
            {busy ? "Applying…" : "Apply"}
          </Button>
        </>
      }
    >
      {err !== undefined && <NotAllowed err={err} what="Apply" />}
      <Field label="Blueprint">
        <Select value={chosen} onChange={setChosen} options={blueprintOptions(blueprints, undefined)} />
      </Field>
    </FormPage>
  );
}

/** Library is the Blueprints tab: the parent's named blueprints, and *New blueprint*. */
function Library({
  state,
  blueprints,
  newBlueprint,
  onRetry,
}: {
  state: { state: "pending" } | { state: "refused"; err: unknown } | { state: "ready"; value: LibraryReply };
  blueprints: readonly LibraryRow[];
  /** The tab's primary action, offered again by the empty state. */
  newBlueprint: ReactNode;
  /** *Try again*: read the library again. */
  onRetry: () => void;
}) {
  const s = useSurface();
  const full = blueprints.length >= MAX_LIBRARY;
  const open = (name: string) => blueprintPath(s.tenant, name);
  return (
    <>
      {full && (
        <Notice kind="info">
          {s.name} keeps {MAX_LIBRARY} blueprints, the most a workspace may. Delete one no workspace uses to
          make another.
        </Notice>
      )}
      {state.state === "pending" && <Pending what="Reading your blueprints" />}
      {state.state === "refused" && (
        <CouldNotShow part="tab" err={blueprintFailure(state.err)} onRetry={onRetry} />
      )}
      {state.state === "ready" && blueprints.length === 0 && (
        <Empty action={newBlueprint}>
          No blueprint yet. A blueprint is configured once, here, and applied to as many workspaces as you
          like; changing it changes every workspace that uses it.
        </Empty>
      )}
      {blueprints.length > 0 && (
        <Table
          label="Blueprints"
          rows={blueprints}
          rowKey={(b) => b.name}
          sort={{ key: "name", dir: "asc" }}
          to={(b) => open(b.name)}
          columns={[
            {
              key: "name",
              header: "Blueprint",
              width: "minmax(0, 1.6fr)",
              value: (b) => b.name,
              cell: (b) => <span className="cell-main mono">{b.name}</span>,
            },
            {
              key: "used",
              header: "Used by",
              width: "140px",
              value: (b) => b.children,
              text: (b) => (b.children === 0 ? "no workspace" : plural(b.children, "workspace")),
              cell: (b) => (
                <span className="muted">
                  {b.children === 0 ? "no workspace" : plural(b.children, "workspace")}
                </span>
              ),
            },
            {
              key: "changed",
              header: "Last changed",
              narrow: false,
              value: (b) => b.created ?? "",
              cell: (b) => (
                <span className="muted">
                  version {b.revision}
                  {b.author !== undefined ? ` · ${b.author}` : ""}
                  {b.created !== undefined ? ` · ${when(b.created)}` : ""}
                </span>
              ),
            },
          ]}
        />
      )}
    </>
  );
}

/** NameBlueprint asks for a new blueprint's name; the editor opens on it, empty, and the first save creates it. */
function NameBlueprint({
  taken,
  onClose,
  onNamed,
}: {
  taken: readonly string[];
  onClose: () => void;
  onNamed: (name: string) => void;
}) {
  const [name, setName] = useState("");
  const clash = taken.includes(name);
  const ok = validName(name) && !clash;
  return (
    <FormPage
      heading="h2"
      back="Blueprints"
      title="New blueprint"
      line="A blueprint is configured once and applied to as many workspaces as you like. Name it; the editor opens on it, and its first save creates it."
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            kind="primary"
            disabled={!ok}
            onClick={() => {
              onNamed(name);
            }}
          >
            Continue
          </Button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) {
            onNamed(name);
          }
        }}
      >
        <Field
          label="Name"
          hint={
            clash
              ? "You keep a blueprint by that name already."
              : "Letters, digits, hyphens and underscores; it never changes. Workspaces are listed with it."
          }
        >
          <TextInput value={name} onChange={setName} placeholder="team" mono required autoFocus />
        </Field>
      </form>
    </FormPage>
  );
}

function Transfer({
  child,
  targets,
  onClose,
  onMoved,
}: {
  child: Child;
  targets: { slug: string; label: string }[];
  onClose: () => void;
  onMoved: (to: string) => void;
}) {
  const s = useSurface();
  const [to, setTo] = useState(targets[0]?.slug ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const submit = () => {
    if (busy || to === "") {
      return;
    }
    setBusy(true);
    setErr(undefined);
    const body: ChildrenTransferRequest = { parent: to };
    s.bus.call<ChildReply>(SERVICE, Subjects.childrenTransfer(child.slug), body).then(
      () => {
        setBusy(false);
        onMoved(targets.find((t) => t.slug === to)?.label ?? to);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <FormPage
      back="Workspaces"
      title={`Transfer ${childName(child)}`}
      line="It moves with everything beneath it. Its blueprint here ends, and it is not managed until its new parent writes one."
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || to === ""}>
            {busy ? "Transferring…" : "Transfer"}
          </Button>
        </>
      }
    >
      {err !== undefined && <Refused err={err} what="Transfer" />}
      {targets.length === 0 ? (
        <Empty>There is no other workspace to hand it to.</Empty>
      ) : (
        <Field label="To">
          <Select
            value={to}
            onChange={setTo}
            options={targets.map((t) => ({ value: t.slug, label: t.label }))}
          />
        </Field>
      )}
    </FormPage>
  );
}
